import ReactPixel from "react-facebook-pixel"
import { environment } from "../../environment/environment"
import { AnalyticsEvent, AnalyticsEventName, AnalyticsItem } from "./analytics"
import { analyticsEventSerializer } from "./serializer"

const DEFAULT_CURRENCY = "GEL"

const FACEBOOK_EVENT_NAMES: { [key: string]: string } = {
  openPage: "PageView",
  addToCard: "AddToCart",
  addToCart: "AddToCart",
  zoomedImage: "ViewContent",
  page_view: "PageView",
  view_item: "ViewContent",
  view_item_list: "ViewCategory",
  select_item: "ViewContent",
  search: "Search",
  add_to_cart: "AddToCart",
  remove_from_cart: "RemoveFromCart",
  add_to_wishlist: "AddToWishlist",
  begin_checkout: "InitiateCheckout",
  add_shipping_info: "AddShippingInfo",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
  login: "Login",
  sign_up: "CompleteRegistration",
}

export class AnalyticsService {
  private initialized = false
  private pixelId = "958842232201007"

  init(pixelId = this.pixelId) {
    if (this.initialized) {
      return
    }
    this.pixelId = pixelId
    try {
      ReactPixel.init(pixelId)
      this.initialized = true
    } catch (error) {
      console.log(error)
    }
  }

  pageView(pagePath = window.location.pathname + window.location.search, pageTitle = document.title) {
    this.track("page_view", { pagePath, pageTitle })
  }

  viewItem(product: any, simple?: any) {
    this.track("view_item", {
      value: this.getPrice(simple || product),
      currency: DEFAULT_CURRENCY,
      items: [this.productToItem(product, simple)],
    })
  }

  search(searchTerm: string, resultCount?: number) {
    this.track("search", {
      searchTerm,
      params: { result_count: resultCount },
    })
  }

  viewItemList(items: any[] = [], listName = "product_list") {
    this.track("view_item_list", {
      currency: DEFAULT_CURRENCY,
      params: { item_list_name: listName },
      items: items.map((item, index) => this.productToItem(item, undefined, 1, index)),
    })
  }

  selectItem(product: any, listName = "product_list", index = 0) {
    this.track("select_item", {
      value: this.getPrice(product),
      currency: DEFAULT_CURRENCY,
      params: { item_list_name: listName },
      items: [this.productToItem(product, undefined, 1, index)],
    })
  }

  addToCart(product: any, simple: any, quantity = 1) {
    this.track("add_to_cart", {
      value: this.getPrice(simple || product) * quantity,
      currency: DEFAULT_CURRENCY,
      items: [this.productToItem(product, simple, quantity)],
    })
  }

  zoomedImage(product?: any, image?: string, index = 0) {
    this.track("zoomedImage", {
      currency: DEFAULT_CURRENCY,
      items: product ? [this.productToItem(product, undefined, 1, index)] : [],
      params: {
        image,
        image_index: index,
      },
    })
  }

  removeFromCart(item: any) {
    this.track("remove_from_cart", {
      value: this.getPrice(item) * (item.quantity || 1),
      currency: DEFAULT_CURRENCY,
      items: [this.cartItemToAnalyticsItem(item)],
    })
  }

  addToWishlist(product: any, simple?: any) {
    this.track("add_to_wishlist", {
      value: this.getPrice(simple || product),
      currency: DEFAULT_CURRENCY,
      items: [this.productToItem(product, simple)],
    })
  }

  beginCheckout(cartItems: any[] = []) {
    this.track("begin_checkout", {
      value: this.getCartValue(cartItems),
      currency: DEFAULT_CURRENCY,
      items: cartItems.map((item, index) => this.cartItemToAnalyticsItem(item, index)),
    })
  }

  addShippingInfo(shippingMethod: any, cartItems: any[] = []) {
    this.track("add_shipping_info", {
      value: this.getCartValue(cartItems),
      currency: DEFAULT_CURRENCY,
      shipping: Number(shippingMethod?.price || 0),
      params: { shipping_tier: shippingMethod?.name || shippingMethod?.uid },
      items: cartItems.map((item, index) => this.cartItemToAnalyticsItem(item, index)),
    })
  }

  addPaymentInfo(paymentMethod: any, cartItems: any[] = []) {
    this.track("add_payment_info", {
      value: this.getCartValue(cartItems),
      currency: DEFAULT_CURRENCY,
      method: paymentMethod?.name || paymentMethod?.uid,
      params: { payment_type: paymentMethod?.uid },
      items: cartItems.map((item, index) => this.cartItemToAnalyticsItem(item, index)),
    })
  }

  purchase(order: any, cartItems: any[] = []) {
    const value = Number(order?.totalToPay || order?.total || this.getCartValue(cartItems))
    this.track("purchase", {
      value,
      currency: DEFAULT_CURRENCY,
      transactionId: order?.id,
      shipping: Number(order?.shippingPrice || order?.shipping || 0),
      coupon: order?.coupon,
      items: cartItems.map((item, index) => this.cartItemToAnalyticsItem(item, index)),
    })
  }

  login(method = "email") {
    this.track("login", { method })
  }

  signUp(method = "email") {
    this.track("sign_up", { method })
  }

  track(name: AnalyticsEventName, data: Partial<AnalyticsEvent> = {}) {
    const event = this.createEvent(name, data)
    this.persist(event)
    this.pushDataLayer(event)
    this.pushGtag(event)
    this.pushFacebookPixel(event)
  }

  private createEvent(name: AnalyticsEventName, data: Partial<AnalyticsEvent>) {
    const event = new AnalyticsEvent()
    event.name = name
    event.eventId = `${name}_${Date.now()}_${Math.random().toString(36).slice(2)}`
    event.pagePath = data.pagePath || window.location.pathname + window.location.search
    event.pageTitle = data.pageTitle || document.title
    event.searchTerm = data.searchTerm
    event.method = data.method
    event.currency = data.currency || DEFAULT_CURRENCY
    event.value = Number(data.value || 0)
    event.transactionId = data.transactionId
    event.coupon = data.coupon
    event.shipping = Number(data.shipping || 0)
    event.tax = Number(data.tax || 0)
    event.items = data.items || []
    event.params = data.params || {}
    return event
  }

  private pushDataLayer(event: AnalyticsEvent) {
    try {
      const win = window as any
      win.dataLayer = win.dataLayer || []
      win.dataLayer.push(this.toPayload(event))
    } catch (error) {
      console.log(error)
    }
  }

  private pushGtag(event: AnalyticsEvent) {
    try {
      const win = window as any
      if (typeof win.gtag === "function") {
        win.gtag("event", event.name, this.toEcommerceParams(event))
      }
    } catch (error) {
      console.log(error)
    }
  }

  private pushFacebookPixel(event: AnalyticsEvent) {
    try {
      if (!this.initialized) {
        this.init()
      }

      const facebookName = FACEBOOK_EVENT_NAMES[event.name]
      if (event.name === "page_view" || event.name === "openPage") {
        ReactPixel.pageView()
        return
      }
      if (!facebookName) {
        ReactPixel.trackCustom(event.name, this.toFacebookParams(event))
        return
      }
      ReactPixel.track(facebookName, this.toFacebookParams(event))
    } catch (error) {
      console.log(error)
    }
  }

  private persist(event: AnalyticsEvent) {
    try {
      const currentUser = this.getCurrentUser()
      const body = analyticsEventSerializer.toJson(event) as any
      body.session_id = this.getSessionId()
      body.client_id = this.getClientId()
      if (currentUser?.id) {
        body.user_id = currentUser.id
      }

      const headers: any = {
        "Content-Type": "application/json",
      }
      if (currentUser?.accessToken) {
        headers.Authorization = `Bearer ${currentUser.accessToken}`
      }

      fetch(`${environment.basePath}/rest_analytics_event`, {
        method: "POST",
        mode: "cors",
        keepalive: true,
        headers,
        body: JSON.stringify(body),
      }).catch((error) => console.log(error))
    } catch (error) {
      console.log(error)
    }
  }

  private getSessionId() {
    const key = "analyticsSessionId"
    let sessionId = sessionStorage.getItem(key)
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2)}`
      sessionStorage.setItem(key, sessionId)
    }
    return sessionId
  }

  private getClientId() {
    const key = "analyticsClientId"
    let clientId = localStorage.getItem(key)
    if (!clientId) {
      clientId = `client_${Date.now()}_${Math.random().toString(36).slice(2)}`
      localStorage.setItem(key, clientId)
    }
    return clientId
  }

  private getCurrentUser() {
    try {
      const currentUser = localStorage.getItem("currentUser")
      return currentUser ? JSON.parse(currentUser) : null
    } catch (error) {
      console.log(error)
      return null
    }
  }

  private toPayload(event: AnalyticsEvent) {
    return {
      event: event.name,
      event_id: event.eventId,
      page_path: event.pagePath,
      page_title: event.pageTitle,
      search_term: event.searchTerm,
      method: event.method,
      ecommerce: this.toEcommerceParams(event),
      ...event.params,
    }
  }

  private toEcommerceParams(event: AnalyticsEvent) {
    return {
      currency: event.currency,
      value: event.value,
      transaction_id: event.transactionId,
      coupon: event.coupon,
      shipping: event.shipping,
      tax: event.tax,
      items: event.items?.map((item) => ({
        item_id: item.itemId,
        item_name: item.itemName,
        item_brand: item.itemBrand,
        item_category: item.itemCategory,
        item_variant: item.itemVariant,
        sku: item.sku,
        price: item.price,
        quantity: item.quantity,
        index: item.index,
      })),
      search_term: event.searchTerm,
      method: event.method,
      ...event.params,
    }
  }

  private toFacebookParams(event: AnalyticsEvent) {
    return {
      content_ids: event.items?.map((item) => item.sku || item.itemId),
      content_name: event.items?.map((item) => item.itemName).join(", "),
      content_type: "product",
      currency: event.currency,
      value: event.value,
      search_string: event.searchTerm,
      status: true,
      ...event.params,
    }
  }

  productToItem(product: any, simple?: any, quantity = 1, index = 0): AnalyticsItem {
    const item = new AnalyticsItem()
    item.itemId = product?.id || product?.uid || simple?.id || simple?.sku
    item.itemName = product?.name || simple?.name
    item.itemBrand = product?.brand?.title || product?.brand?.id
    item.itemCategory = product?.category?.title || product?.category?.uid
    item.itemVariant = simple?.name
    item.sku = simple?.sku || product?.sku
    item.price = this.getPrice(simple || product)
    item.quantity = quantity
    item.index = index
    return item
  }

  cartItemToAnalyticsItem(cartItem: any, index = 0): AnalyticsItem {
    return this.productToItem(cartItem?.product || cartItem, {
      sku: cartItem?.sku,
      name: cartItem?.product?.name,
      price: cartItem?.price,
      specialPrice: cartItem?.specialPrice,
    }, cartItem?.quantity || 1, index)
  }

  private getPrice(item: any) {
    return Number(item?.specialPrice || item?.special_price || item?.price || 0)
  }

  private getCartValue(cartItems: any[] = []) {
    return cartItems.reduce((total, item) => total + this.getPrice(item) * (item.quantity || 1), 0)
  }
}

export const analyticsService = new AnalyticsService()
